import React from 'react'
import { Button, Empty, Modal, Tag } from 'antd'
import styled from 'styled-components/macro'
import {
  formatInvoiceTotalDisplay,
  formatSupplierTitleDisplay,
} from '../../utils/invoiceUploadHelpers'
import {
  getAddPagesOfferMeta,
  hasAddPagesOffer,
} from '../../utils/invoiceMultiDocRules'

function formatDuplicateConfidence(confidence) {
  const value = String(confidence || 'possible')
    .trim()
    .toLowerCase()
  if (value === 'exact') return 'Exact match'
  if (value === 'high') return 'High confidence'
  return 'Possible match'
}

function DuplicateResolutionModal({
  open,
  invoice,
  invoices = [],
  venueId,
  onClose,
  onDismissDuplicate,
  onMergePages,
  dismissing = false,
  merging = false,
}) {
  const addPages = getAddPagesOfferMeta(invoice || {})
  const isAddPages = hasAddPagesOffer(invoice)
  const busy = dismissing || merging
  const duplicateIds = isAddPages
    ? addPages.targetInvoiceId
      ? [addPages.targetInvoiceId]
      : invoice?.matchSummary?.addPagesMatchInvoiceIds || []
    : invoice?.matchSummary?.duplicateMatchInvoiceIds || []
  const invoiceById = new Map(
    (invoices || []).map(item => [String(item.id), item]),
  )

  return (
    <Modal
      visible={open}
      title={
        isAddPages ? 'Add to existing invoice?' : 'Review possible duplicate'
      }
      width={680}
      footer={
        <FooterActions>
          <Button onClick={onClose} disabled={busy}>
            Close
          </Button>
          {onDismissDuplicate && (
            <Button
              loading={dismissing}
              disabled={merging}
              onClick={() => onDismissDuplicate(invoice)}
            >
              {isAddPages
                ? 'This is a different invoice'
                : 'Not a duplicate — continue matching'}
            </Button>
          )}
          {isAddPages && onMergePages ? (
            <Button
              type='primary'
              loading={merging}
              disabled={dismissing}
              onClick={() => onMergePages(invoice)}
            >
              {addPages.buttonLabel}
            </Button>
          ) : null}
        </FooterActions>
      }
      onCancel={busy ? undefined : onClose}
      destroyOnClose
    >
      <Intro>
        {isAddPages
          ? addPages.guidance
          : 'This invoice may already exist in OMS. Review the existing record before linking, reconciling, or archiving either invoice. If this is a different document, mark it as not a duplicate and we\u2019ll continue matching.'}
      </Intro>

      <CurrentInvoice>
        <Label>{isAddPages ? 'New photo / upload' : 'New upload'}</Label>
        <strong>{invoice?.invoiceNumber || 'No invoice number'}</strong>
        <span>{formatSupplierTitleDisplay(invoice?.supplierTitle)}</span>
        <span>{formatInvoiceTotalDisplay(invoice?.invoiceTotal)}</span>
      </CurrentInvoice>

      {duplicateIds.length ? (
        <DuplicateList>
          {duplicateIds.map(duplicateId => {
            const existing = invoiceById.get(String(duplicateId))
            return (
              <DuplicateCard key={duplicateId} $highlight={isAddPages}>
                <DuplicateHeader>
                  <div>
                    <Label>Existing invoice</Label>
                    <strong>
                      {existing?.invoiceNumber ||
                        addPages.targetInvoiceNumber ||
                        `Invoice ${duplicateId}`}
                    </strong>
                  </div>
                  <ConfidenceTag $addPages={isAddPages}>
                    {isAddPages
                      ? 'Same invoice #'
                      : formatDuplicateConfidence(
                          invoice?.matchSummary?.duplicateConfidence,
                        )}
                  </ConfidenceTag>
                </DuplicateHeader>
                <MetaGrid>
                  <span>Supplier</span>
                  <strong>
                    {formatSupplierTitleDisplay(existing?.supplierTitle)}
                  </strong>
                  <span>PO</span>
                  <strong>{existing?.purchaseOrderNumber || '—'}</strong>
                  <span>Total</span>
                  <strong>
                    {formatInvoiceTotalDisplay(existing?.invoiceTotal)}
                  </strong>
                  <span>Status</span>
                  <strong>{existing?.status || 'Unknown'}</strong>
                </MetaGrid>
                <Button
                  type='default'
                  href={`/invoice/${venueId}/${duplicateId}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  View existing invoice
                </Button>
              </DuplicateCard>
            )
          })}
        </DuplicateList>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='The existing invoice reference is unavailable — it may have been archived. You can mark this as a different invoice to continue matching.'
        />
      )}
    </Modal>
  )
}

export default DuplicateResolutionModal

const Intro = styled.p`
  margin: 0 0 16px;
  color: rgba(0, 0, 0, 0.65);
`

const CurrentInvoice = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  margin-bottom: 16px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fafafa;
`

const Label = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.45);
`

const DuplicateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const DuplicateCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border: 1px solid ${props => (props.$highlight ? '#91d5ff' : '#d9d9d9')};
  border-radius: 8px;
  background: ${props => (props.$highlight ? '#e6f7ff' : '#fff')};
`

const DuplicateHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`

const ConfidenceTag = styled(Tag)`
  flex: 0 0 auto;
  margin: 0;
  color: ${props => (props.$addPages ? '#096dd9' : '#531dab')};
  background: ${props => (props.$addPages ? '#e6f7ff' : '#f9f0ff')};
  border-color: ${props => (props.$addPages ? '#91d5ff' : '#d3adf7')};
`

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 6px 12px;
  font-size: 13px;

  span {
    color: rgba(0, 0, 0, 0.45);
  }
`

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
`
